import { useState, useEffect, useCallback } from 'react';
import {
  Loader2, Eye, EyeOff, Trash2, X, Sparkles, Zap, CalendarDays, Clock,
  FileText, Check,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BLOG_TOPICS = [
  { id: 'einvoicing', name: 'eInvoicing Mandates' },
  { id: 'sap-s4hana', name: 'SAP S/4HANA & Tax Technology' },
  { id: 'vat', name: 'VAT Compliance' },
  { id: 'strategy', name: 'Compliance Strategy' },
  { id: 'regulatory', name: 'Regulatory Updates' },
];

const STATUS_META = {
  draft:     { label: 'Draft',     Icon: FileText,     cls: 'bg-slate-100 text-slate-500' },
  scheduled: { label: 'Scheduled', Icon: CalendarDays, cls: 'bg-taxera-blue/10 text-taxera-blue' },
  published: { label: 'Live',      Icon: Eye,          cls: 'bg-green-50 text-green-600' },
  archived:  { label: 'Archived',  Icon: EyeOff,       cls: 'bg-slate-100 text-slate-400' },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.draft;
  const { Icon } = meta;
  return (
    <span className={`flex items-center gap-1 text-2xs font-bold px-2 py-0.5 rounded-full ${meta.cls}`} data-testid={`blog-status-${status}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
};

const BlogTab = ({ headers }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genTopic, setGenTopic] = useState('');
  const [customTopic, setCustomTopic] = useState('');
  const [showGenerator, setShowGenerator] = useState(false);
  const [genSettings, setGenSettings] = useState({ auto_generate: true, active_topics: BLOG_TOPICS.map(t => t.id) });
  const [settingsLoading, setSettingsLoading] = useState(false);
  // Per-article schedule editor: { [slug]: { publish_at: ISOstring } }
  const [scheduleDrafts, setScheduleDrafts] = useState({});
  const [savingSlug, setSavingSlug] = useState(null);

  const fetchArticles = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/blog`, { headers, credentials: 'include' });
      if (res.ok) { const data = await res.json(); setArticles(data.articles || []); }
    } catch (e) { console.error('Failed to fetch blog articles:', e); } finally { setLoading(false); }
  }, [headers]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/blog/admin/generation-settings`, { headers, credentials: 'include' });
      if (res.ok) { const data = await res.json(); setGenSettings(data); }
    } catch (e) { console.error('Failed to fetch blog settings:', e); }
  }, [headers]);

  useEffect(() => { fetchArticles(); fetchSettings(); }, [fetchArticles, fetchSettings]);

  const updateArticle = async (slug, patch) => {
    setSavingSlug(slug);
    try {
      const res = await fetch(`${API}/api/admin/blog/${slug}`, {
        method: 'PUT', headers, credentials: 'include', body: JSON.stringify(patch),
      });
      if (res.ok) {
        // Optimistically merge into local state (avoid full refetch flicker)
        setArticles(a => a.map(x => x.slug === slug ? { ...x, ...patch } : x));
      }
    } catch (e) {
      console.error('Failed to update article:', e);
    } finally {
      setSavingSlug(null);
    }
  };

  const setStatus = async (slug, status) => {
    if (status === 'scheduled') {
      // Open inline schedule editor; do not save until user picks a date.
      const existing = articles.find(a => a.slug === slug);
      setScheduleDrafts(d => ({
        ...d,
        [slug]: { publish_at: existing?.publish_at || '' },
      }));
      return;
    }
    setScheduleDrafts(d => { const c = { ...d }; delete c[slug]; return c; });
    await updateArticle(slug, { status, publish_at: null });
  };

  const saveSchedule = async (slug) => {
    const draft = scheduleDrafts[slug];
    if (!draft?.publish_at) return;
    await updateArticle(slug, { status: 'scheduled', publish_at: draft.publish_at });
    setScheduleDrafts(d => { const c = { ...d }; delete c[slug]; return c; });
  };

  const cancelSchedule = (slug) => {
    setScheduleDrafts(d => { const c = { ...d }; delete c[slug]; return c; });
  };

  const deleteArticle = async (slug) => {
    if (!window.confirm('Delete this article?')) return;
    await fetch(`${API}/api/blog/admin/articles/${slug}`, { method: 'DELETE', headers, credentials: 'include' });
    setArticles(a => a.filter(x => x.slug !== slug));
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const body = customTopic ? { custom_topic: customTopic } : genTopic ? { topic_id: genTopic } : {};
      const res = await fetch(`${API}/api/blog/admin/generate`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(body) });
      if (res.ok) {
        const data = await res.json();
        if (data.article) setArticles(prev => [data.article, ...prev]);
        setCustomTopic(''); setShowGenerator(false);
      }
    } catch (e) { console.error('Failed to generate blog article:', e); } finally { setGenerating(false); }
  };

  const saveSettings = async () => {
    setSettingsLoading(true);
    try {
      await fetch(`${API}/api/blog/admin/generation-settings`, { method: 'PUT', headers, credentials: 'include', body: JSON.stringify(genSettings) });
    } catch (e) { console.error('Failed to save blog settings:', e); } finally { setSettingsLoading(false); }
  };

  const toggleTopic = (id) => {
    setGenSettings(prev => ({
      ...prev,
      active_topics: prev.active_topics.includes(id)
        ? prev.active_topics.filter(t => t !== id)
        : [...prev.active_topics, id],
    }));
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-taxera-blue" />;

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400">{articles.length} articles</span>
        <button onClick={() => setShowGenerator(!showGenerator)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-taxera-teal text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all"
          data-testid="blog-generate-btn">
          {showGenerator ? <X className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
          {showGenerator ? 'Cancel' : 'Generate Article'}
        </button>
      </div>

      {showGenerator && (
        <div className="bg-gradient-to-r from-taxera-blue/5 to-taxera-teal/5 rounded-2xl border border-taxera-blue/20 p-5 mb-5" data-testid="blog-generator">
          <h4 className="font-bold text-sm text-taxera-charcoal mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-taxera-teal" /> AI Blog Generator</h4>
          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-2xs font-bold text-slate-400 uppercase tracking-wide block mb-1">Topic category</label>
              <select value={genTopic} onChange={(e) => setGenTopic(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" data-testid="blog-gen-topic">
                <option value="">Random (any topic)</option>
                {BLOG_TOPICS.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-2xs font-bold text-slate-400 uppercase tracking-wide block mb-1">Or custom topic</label>
              <input type="text" value={customTopic} onChange={(e) => setCustomTopic(e.target.value)} placeholder="e.g., France eInvoicing deadline September 2026"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" data-testid="blog-gen-custom" />
            </div>
          </div>
          <button onClick={handleGenerate} disabled={generating}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-taxera-blue text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50"
            data-testid="blog-gen-submit">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            {generating ? 'Generating...' : 'Generate Now'}
          </button>
        </div>
      )}

      <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <button onClick={() => { setGenSettings(p => ({ ...p, auto_generate: !p.auto_generate })); }}
              className={`w-10 h-6 rounded-full transition-colors relative ${genSettings.auto_generate ? 'bg-taxera-teal' : 'bg-slate-300'}`}
              data-testid="blog-auto-toggle">
              <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${genSettings.auto_generate ? 'left-5' : 'left-1'}`} />
            </button>
            <div>
              <span className="text-sm font-semibold text-slate-700">Daily auto-generation</span>
              <p className="text-2xs text-slate-400">AI generates one article per day from active topics</p>
            </div>
          </div>
          <button onClick={saveSettings} disabled={settingsLoading}
            className="text-xs px-3 py-1.5 bg-taxera-charcoal text-white rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
            data-testid="blog-save-settings">
            {settingsLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {BLOG_TOPICS.map(t => (
            <button key={t.id} onClick={() => toggleTopic(t.id)}
              className={`text-2xs px-2.5 py-1 rounded-full font-semibold transition-colors ${genSettings.active_topics.includes(t.id) ? 'bg-taxera-blue text-white' : 'bg-white text-slate-400 border border-slate-200'}`}
              data-testid={`blog-topic-${t.id}`}>
              {t.name}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {articles.map((a) => {
          const status = a.status || (a.published ? 'published' : 'draft');
          const scheduleDraft = scheduleDrafts[a.slug];
          const saving = savingSlug === a.slug;
          return (
            <div key={a.slug} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`blog-row-${a.slug}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-taxera-charcoal truncate">{a.title}</div>
                    <StatusBadge status={status} />
                    {a.auto_generated && <span className="shrink-0 text-2xs px-1.5 py-0.5 bg-purple-50 text-purple-500 rounded-full font-semibold">AI</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-2xs px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded-full">{a.category}</span>
                    {a.reading_time && <span className="text-2xs text-slate-400">{a.reading_time} min read</span>}
                    {a.view_count > 0 && <span className="text-2xs text-slate-400">{a.view_count} views</span>}
                    {status === 'scheduled' && a.publish_at && (
                      <span className="text-2xs text-taxera-blue font-medium flex items-center gap-1">
                        <CalendarDays className="w-3 h-3" />
                        Publishes {new Date(a.publish_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                      </span>
                    )}
                    {status === 'published' && a.published_at && (
                      <span className="text-2xs text-slate-300 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Published {new Date(a.published_at).toLocaleDateString('en-GB', { dateStyle: 'medium' })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select
                    value={status}
                    onChange={(e) => setStatus(a.slug, e.target.value)}
                    disabled={saving}
                    className="text-xs px-2.5 py-1.5 bg-white rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-taxera-blue/20"
                    data-testid={`blog-status-select-${a.slug}`}
                  >
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="published">Published (now)</option>
                    <option value="archived">Archived</option>
                  </select>
                  <button onClick={() => deleteArticle(a.slug)}
                    className="p-1.5 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 transition-colors"
                    data-testid={`blog-delete-${a.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {scheduleDraft && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3 flex-wrap" data-testid={`blog-schedule-${a.slug}`}>
                  <CalendarDays className="w-4 h-4 text-taxera-blue shrink-0" />
                  <input
                    type="datetime-local"
                    value={scheduleDraft.publish_at ? scheduleDraft.publish_at.slice(0, 16) : ''}
                    onChange={(e) => setScheduleDrafts(d => ({
                      ...d,
                      [a.slug]: { publish_at: e.target.value ? new Date(e.target.value).toISOString() : '' },
                    }))}
                    className="px-3 py-2 bg-white rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-taxera-blue/20"
                    data-testid={`blog-schedule-input-${a.slug}`}
                  />
                  <button
                    onClick={() => saveSchedule(a.slug)}
                    disabled={!scheduleDraft.publish_at || saving}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-taxera-blue text-white rounded-lg text-xs font-semibold disabled:opacity-50"
                    data-testid={`blog-schedule-save-${a.slug}`}
                  >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    Schedule
                  </button>
                  <button
                    onClick={() => cancelSchedule(a.slug)}
                    className="text-xs text-slate-400 hover:text-slate-600"
                    data-testid={`blog-schedule-cancel-${a.slug}`}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BlogTab;
